import _defineProperty from '@babel/runtime-corejs2/helpers/defineProperty';
import _objectWithoutProperties from '@babel/runtime-corejs2/helpers/objectWithoutProperties';
import { forwardRef, useContext, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styledSystemPropTypes from '@styled-system/prop-types';
import Box from '@locus-labs/mol-desktop-box';
import styled, { ThemeContext, css } from 'styled-components';
import Text from '@locus-labs/mol-desktop-text';
import Icon from '@locus-labs/mol-desktop-icon';
import { rgba } from 'polished';
import Tooltip from '@locus-labs/mol-desktop-tooltip';
import { jsx, jsxs } from 'react/jsx-runtime';
import { theme, withProp, ifProp } from 'styled-tools';
import smoothscroll from 'smoothscroll-polyfill';

const _excluded$7 = ["resultsNumber"];
function ownKeys$7(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$7(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$7(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$7(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const StyledSearchResultsIndicator$2 = styled(Box).withConfig({
  displayName: "search-results-indicator__StyledSearchResultsIndicator",
  componentId: "fxrpw6-0"
})(["flex:0 0 auto;"]);
const SearchResultsIndicator = /*#__PURE__*/forwardRef((props, ref) => {
  const themeContext = useContext(ThemeContext);
  const {
      resultsNumber
    } = props,
    omittedProps = _objectWithoutProperties(props, _excluded$7);
  return /*#__PURE__*/jsx(StyledSearchResultsIndicator$2, _objectSpread$7(_objectSpread$7({
    "data-cy": "SearchResultsIndicator",
    ref: ref,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    borderRadius: "100%",
    bg: themeContext.colors.mapNavSearchResults,
    color: themeContext.colors.mapNavSearchResultsText
  }, omittedProps), {}, {
    children: typeof resultsNumber !== 'undefined' && /*#__PURE__*/jsx(Text, {
      fontSize: "sm",
      children: resultsNumber > 99 ? '99+' : resultsNumber
    })
  }));
});
SearchResultsIndicator.displayName = 'SearchResultsIndicator';
SearchResultsIndicator.defaultProps = {};
SearchResultsIndicator.propTypes = _objectSpread$7(_objectSpread$7(_objectSpread$7(_objectSpread$7(_objectSpread$7(_objectSpread$7(_objectSpread$7(_objectSpread$7(_objectSpread$7(_objectSpread$7(_objectSpread$7({}, styledSystemPropTypes.space), styledSystemPropTypes.color), styledSystemPropTypes.background), styledSystemPropTypes.border), styledSystemPropTypes.flexbox), styledSystemPropTypes.grid), styledSystemPropTypes.layout), styledSystemPropTypes.position), styledSystemPropTypes.shadow), styledSystemPropTypes.typography), {}, {
  as: PropTypes.string,
  forwardAs: PropTypes.string,
  resultsNumber: PropTypes.number
});

const _excluded$6 = ["tooltipContent", "currentBuildingName", "fallbackBuildingName", "currentLevelName", "fallbackLevelName", "isExpanded", "onTriggerClick", "withSearchResults"];
function ownKeys$6(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$6(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$6(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$6(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const StyledWrapper = styled(Box).withConfig({
  displayName: "trigger__StyledWrapper",
  componentId: "sc-11274t5-0"
})(["filter:drop-shadow(", ");"], theme('shadows.md'));
const StyledBox$1 = styled(Box).withConfig({
  displayName: "trigger__StyledBox",
  componentId: "sc-11274t5-1"
})(["border-radius:", ";overflow:hidden;"], theme('radii.lg'));
const StyledText$2 = styled(Text).withConfig({
  displayName: "trigger__StyledText",
  componentId: "sc-11274t5-2"
})(["word-break:break-all;"]);
const Toggler = styled(Box).withConfig({
  displayName: "trigger__Toggler",
  componentId: "sc-11274t5-3"
})(["cursor:pointer;position:relative;&:focus{outline:none;border:none;}&:focus-visible{&:before{display:initial;}}&:hover{background-color:", ";}&:before{display:none;content:'';outline:2px solid darkorange;border-radius:0 8px 8px 0;outline-offset:-2px;height:100%;width:36px;position:absolute;}"], theme('colors.mapNavBackground'));
const StyledSearchResultsIndicator$1 = styled(SearchResultsIndicator).withConfig({
  displayName: "trigger__StyledSearchResultsIndicator",
  componentId: "sc-11274t5-4"
})(["pointer-events:none;"]);
const Trigger = /*#__PURE__*/forwardRef((props, ref) => {
  const themeContext = useContext(ThemeContext);
  const {
      tooltipContent,
      currentBuildingName,
      fallbackBuildingName,
      currentLevelName,
      fallbackLevelName,
      isExpanded,
      onTriggerClick,
      withSearchResults
    } = props,
    omittedProps = _objectWithoutProperties(props, _excluded$6);
  return /*#__PURE__*/jsxs(StyledWrapper, _objectSpread$6(_objectSpread$6({
    ref: ref,
    "data-cy": "BuildingLevelSelectorTrigger"
  }, omittedProps), {}, {
    children: [/*#__PURE__*/jsxs(StyledBox$1, {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      position: "relative",
      children: [/*#__PURE__*/jsxs(Box, {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: "6px 8px",
        bg: themeContext.colors.background,
        flex: "1 1 auto",
        children: [/*#__PURE__*/jsx(StyledText$2, {
          fontSize: "md",
          fontWeight: 500,
          lineHeight: "18px",
          color: themeContext.colors.primaryText,
          limitLinesOfTextTo: 1,
          children: currentBuildingName || fallbackBuildingName
        }), /*#__PURE__*/jsx(StyledText$2, {
          fontSize: "sm",
          color: themeContext.colors.secondaryText,
          limitLinesOfTextTo: 1,
          children: currentLevelName || fallbackLevelName
        })]
      }), /*#__PURE__*/jsx(Box, {
        flex: "0 0 auto",
        display: "flex",
        alignItems: "stretch",
        children: /*#__PURE__*/jsx(Tooltip, {
          content: tooltipContent,
          placement: "bottom-end",
          cancelTooltip: isExpanded,
          children: /*#__PURE__*/jsx(Toggler, {
            "data-cy": "TriggerButton",
            as: "button",
            onClick: onTriggerClick,
            display: "flex",
            alignItems: "stretch",
            height: "100%",
            bg: rgba(themeContext.colors.mapNavBackground, 0.85),
            outline: "none",
            border: "none",
            padding: "0",
            "aria-expanded": isExpanded,
            "aria-label": "Toggle buildings list dropdown",
            children: /*#__PURE__*/jsx(Box, {
              padding: "2px",
              height: "100%",
              display: "flex",
              alignItems: "center",
              children: /*#__PURE__*/jsx(Icon, {
                name: "triangle.down",
                iconSize: "md",
                degreesOfIconRotation: props.degreesOfIconRotation,
                color: themeContext.colors.mapNavText,
                "aria-hidden": "true"
              })
            })
          })
        })
      })]
    }), withSearchResults && /*#__PURE__*/jsx(StyledSearchResultsIndicator$1, {
      position: "absolute",
      top: "36px",
      right: "-4px",
      width: "16px",
      height: "16px"
    })]
  }));
});
Trigger.displayName = 'Trigger';
Trigger.defaultProps = {
  isExpanded: false,
  withSearchResults: false,
  fallbackBuildingName: 'Select a building...',
  fallbackLevelName: '...',
  tooltipContent: 'Show Building & Levels'
};
Trigger.propTypes = _objectSpread$6(_objectSpread$6(_objectSpread$6(_objectSpread$6(_objectSpread$6(_objectSpread$6(_objectSpread$6(_objectSpread$6(_objectSpread$6(_objectSpread$6(_objectSpread$6({}, styledSystemPropTypes.space), styledSystemPropTypes.color), styledSystemPropTypes.background), styledSystemPropTypes.border), styledSystemPropTypes.flexbox), styledSystemPropTypes.grid), styledSystemPropTypes.layout), styledSystemPropTypes.position), styledSystemPropTypes.shadow), styledSystemPropTypes.typography), {}, {
  as: PropTypes.string,
  forwardAs: PropTypes.string,
  tooltipContent: PropTypes.string,
  currentBuildingName: PropTypes.string,
  fallbackBuildingName: PropTypes.string,
  currentLevelName: PropTypes.string,
  fallbackLevelName: PropTypes.string,
  isExpanded: PropTypes.bool,
  onTriggerClick: PropTypes.func,
  withSearchResults: PropTypes.bool
});

const _excluded$5 = ["id", "name", "resultsNumber", "selectedBuildingId", "onBuildingClick"];
function ownKeys$5(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$5(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$5(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$5(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const StyledButton = styled('button').withConfig({
  displayName: "building-row__StyledButton",
  componentId: "sc-1qfppif-0"
})(["all:unset;width:100%;cursor:pointer;display:flex;align-items:center;color:", ";border-bottom:1px solid ", ";background-color:", ";&:hover{background-color:", ";}&:focus-visible{outline:2px solid darkorange;outline-offset:-3px;}", ""], theme('colors.primaryButtonText'), theme('colors.mapNavBackground'), withProp(theme('colors.mapNavBackground'), mapNavBackground => rgba(mapNavBackground, 0.85)), theme('colors.mapNavBackground'), ifProp('isSelected', css(["background-color:", ";"], theme('colors.mapNavBackground')), ""));
const StyledText$1 = styled(Text).withConfig({
  displayName: "building-row__StyledText",
  componentId: "sc-1qfppif-1"
})(["flex:1 1 auto;word-break:break-all;"]);
const StyledSearchResultsIndicator = styled(SearchResultsIndicator).withConfig({
  displayName: "building-row__StyledSearchResultsIndicator",
  componentId: "sc-1qfppif-2"
})(["flex:0 0 auto;"]);
const BuildingRow = /*#__PURE__*/forwardRef((props, ref) => {
  const themeContext = useContext(ThemeContext);
  const {
      id,
      name,
      resultsNumber,
      selectedBuildingId,
      onBuildingClick
    } = props,
    omittedProps = _objectWithoutProperties(props, _excluded$5);
  return /*#__PURE__*/jsxs(StyledButton, _objectSpread$5(_objectSpread$5({
    "data-cy": "BuildingRow",
    ref: ref,
    display: "flex",
    alignItems: "center",
    color: themeContext.colors.mapNavText,
    padding: "2px 6px 1px 2px",
    onClick: () => onBuildingClick(id),
    isSelected: selectedBuildingId && selectedBuildingId === id,
    role: "option",
    "aria-label": name,
    "aria-selected": id === selectedBuildingId
  }, omittedProps), {}, {
    children: [/*#__PURE__*/jsx(Icon, {
      name: "triangle.down",
      iconSize: "md",
      color: themeContext.colors.mapNavText,
      as: "span",
      height: "32px",
      width: "32px",
      display: "inline-block",
      position: "relative",
      verticalAlign: "middle",
      degreesOfIconRotation: selectedBuildingId && selectedBuildingId === id ? 0 : -90
    }), /*#__PURE__*/jsx(StyledText$1, {
      fontSize: "md",
      fontWeight: "500",
      limitLinesOfTextTo: 1,
      "aria-hidden": "true",
      children: name
    }), resultsNumber && /*#__PURE__*/jsx(StyledSearchResultsIndicator, {
      marginLeft: "2px",
      resultsNumber: resultsNumber
    })]
  }));
});
BuildingRow.displayName = 'Building Row';
BuildingRow.defaultProps = {};
BuildingRow.propTypes = _objectSpread$5(_objectSpread$5(_objectSpread$5(_objectSpread$5(_objectSpread$5(_objectSpread$5(_objectSpread$5(_objectSpread$5(_objectSpread$5(_objectSpread$5(_objectSpread$5({}, styledSystemPropTypes.space), styledSystemPropTypes.color), styledSystemPropTypes.background), styledSystemPropTypes.border), styledSystemPropTypes.flexbox), styledSystemPropTypes.grid), styledSystemPropTypes.layout), styledSystemPropTypes.position), styledSystemPropTypes.shadow), styledSystemPropTypes.typography), {}, {
  as: PropTypes.string,
  forwardAs: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  resultsNumber: PropTypes.number,
  selectedBuildingId: PropTypes.string,
  onBuildingClick: PropTypes.func
});

const _excluded$4 = ["id", "name", "details", "resultsNumber", "selectedLevelId", "onLevelClick", "buildingName"];
function ownKeys$4(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$4(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$4(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$4(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const StyledLevelRow$1 = styled(Box).withConfig({
  displayName: "level-row__StyledLevelRow",
  componentId: "sc-1dvdi4b-0"
})(["border-bottom:", ";&:focus-visible{outline:2px solid darkorange;outline-offset:-3px;}"], withProp(theme('colors.primaryText'), primaryText => "1px solid ".concat(rgba(primaryText, 0.1))));
const Wrapper = styled(Box).withConfig({
  displayName: "level-row__Wrapper",
  componentId: "sc-1dvdi4b-1"
})(["cursor:pointer;&:hover{background-color:", ";}", ""], withProp(theme('colors.primary'), primary => rgba(primary, 0.05)), ifProp('isSelected', css(["background-color:", ";&::before{content:'';position:absolute;top:0;left:0;height:100%;width:3px;border-top-right-radius:4px;border-bottom-right-radius:4px;background-color:", ";}"], withProp(theme('colors.primary'), primary => rgba(primary, 0.05)), theme('colors.primary')), ""));
const StyledText = styled(Text).withConfig({
  displayName: "level-row__StyledText",
  componentId: "sc-1dvdi4b-2"
})(["word-break:break-all;"]);
const LevelRow = /*#__PURE__*/forwardRef((props, ref) => {
  const themeContext = useContext(ThemeContext);
  const {
      id,
      name,
      details,
      resultsNumber,
      selectedLevelId,
      onLevelClick,
      buildingName
    } = props,
    omittedProps = _objectWithoutProperties(props, _excluded$4);
  return /*#__PURE__*/jsx(StyledLevelRow$1, _objectSpread$4(_objectSpread$4({
    "data-cy": "LevelRow",
    ref: ref,
    bg: themeContext.colors.background
  }, omittedProps), {}, {
    role: "option",
    "aria-label": "".concat(buildingName, " ").concat(name, " ").concat(details),
    tabIndex: "0",
    onClick: () => onLevelClick(id),
    onKeyDown: e => e.key === 'Enter' || e.key === ' ' ? onLevelClick(id) : null,
    "aria-selected": selectedLevelId && selectedLevelId === id,
    children: /*#__PURE__*/jsxs(Wrapper, {
      display: "flex",
      position: "relative",
      alignItems: "center",
      justifyContent: "space-between",
      p: "6px 0px 5px 16px",
      isSelected: selectedLevelId && selectedLevelId === id,
      children: [/*#__PURE__*/jsxs(Box, {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        paddingRight: "6px",
        children: [/*#__PURE__*/jsx(StyledText, {
          fontSize: "md",
          fontWeight: 500,
          lineHeight: "18px",
          color: themeContext.colors.primaryText,
          limitLinesOfTextTo: 1,
          "aria-hidden": "true",
          children: name
        }), /*#__PURE__*/jsx(StyledText, {
          fontSize: "sm",
          color: themeContext.colors.secondaryText,
          limitLinesOfTextTo: 1,
          "aria-hidden": "true",
          children: details
        })]
      }), resultsNumber && /*#__PURE__*/jsx(SearchResultsIndicator, {
        marginRight: "6px",
        resultsNumber: resultsNumber
      })]
    })
  }));
});
LevelRow.displayName = 'Level Row';
LevelRow.defaultProps = {
  isSelected: false
};
LevelRow.propTypes = _objectSpread$4(_objectSpread$4(_objectSpread$4(_objectSpread$4(_objectSpread$4(_objectSpread$4(_objectSpread$4(_objectSpread$4(_objectSpread$4(_objectSpread$4(_objectSpread$4({}, styledSystemPropTypes.space), styledSystemPropTypes.color), styledSystemPropTypes.background), styledSystemPropTypes.border), styledSystemPropTypes.flexbox), styledSystemPropTypes.grid), styledSystemPropTypes.layout), styledSystemPropTypes.position), styledSystemPropTypes.shadow), styledSystemPropTypes.typography), {}, {
  as: PropTypes.string,
  forwardAs: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  details: PropTypes.string,
  selectedLevelId: PropTypes.string,
  onLevelClick: PropTypes.func,
  resultsNumber: PropTypes.number
});

const _excluded$3 = ["levels", "onLevelClick", "selectedLevelId", "buildingName"];
function ownKeys$3(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$3(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$3(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$3(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const StyledLevelRow = styled(LevelRow).withConfig({
  displayName: "levels-list__StyledLevelRow",
  componentId: "sc-1cv1zq0-0"
})(["&:last-of-type{border-bottom:none;}"]);
const LevelsList = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      levels,
      onLevelClick,
      selectedLevelId,
      buildingName
    } = props,
    omittedProps = _objectWithoutProperties(props, _excluded$3);
  return /*#__PURE__*/jsx(Box, _objectSpread$3(_objectSpread$3({
    "data-cy": "LevelsList",
    ref: ref
  }, omittedProps), {}, {
    children: Object.values(levels).map(level => /*#__PURE__*/jsx(StyledLevelRow, _objectSpread$3({
      buildingName: buildingName,
      selectedLevelId: selectedLevelId,
      onLevelClick: onLevelClick
    }, level), level.id))
  }));
});
LevelsList.displayName = 'Levels List';
LevelsList.defaultProps = {};
LevelsList.propTypes = _objectSpread$3(_objectSpread$3(_objectSpread$3(_objectSpread$3(_objectSpread$3(_objectSpread$3(_objectSpread$3(_objectSpread$3(_objectSpread$3(_objectSpread$3(_objectSpread$3({}, styledSystemPropTypes.space), styledSystemPropTypes.color), styledSystemPropTypes.background), styledSystemPropTypes.border), styledSystemPropTypes.flexbox), styledSystemPropTypes.grid), styledSystemPropTypes.layout), styledSystemPropTypes.position), styledSystemPropTypes.shadow), styledSystemPropTypes.typography), {}, {
  as: PropTypes.string,
  forwardAs: PropTypes.string,
  levels: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    details: PropTypes.string.isRequired,
    resultsNumber: PropTypes.number
  })),
  selectedLevelId: PropTypes.string,
  onLevelClick: PropTypes.func
});

const _excluded$2 = ["id", "name", "onBuildingClick", "selectedBuildingId", "selectedLevelId", "onLevelClick", "resultsNumber", "levels"];
function ownKeys$2(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$2(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$2(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$2(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const StyledBox = styled(Box).withConfig({
  displayName: "building-accordion__StyledBox",
  componentId: "mjb04o-0"
})(["&::before{content:'';position:absolute;top:0;width:100%;height:100%;pointer-events:none;box-shadow:inset 0 18px 20px -20px ", ",inset 0 -18px 20px -20px ", ";}"], rgba('#2e3032', 0.4), rgba('#2e3032', 0.4));
const StyledAccordion = styled(Box).withConfig({
  displayName: "building-accordion__StyledAccordion",
  componentId: "mjb04o-1"
})(["&:last-child{", "::before{box-shadow:inset 0 18px 20px -20px ", ",inset 0 -18px 20px -20px ", ";}}"], StyledBox, rgba('#2e3032', 0.4), rgba('#2e3032', 0));
const BuildingAccordion = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      id,
      name,
      onBuildingClick,
      selectedBuildingId,
      selectedLevelId,
      onLevelClick,
      resultsNumber,
      levels
    } = props,
    omittedProps = _objectWithoutProperties(props, _excluded$2);
  const accordionRef = useRef(ref);
  const levelsRef = useRef(ref);
  smoothscroll.polyfill();
  useEffect(() => {
    if (levelsRef && levelsRef.current && accordionRef && accordionRef.current) {
      if (accordionRef.current.parentNode.scrollTop > accordionRef.current.offsetTop) {
        // if accordion was already scrolled past, move the view back to the start of element
        accordionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      } else if (accordionRef.current.offsetTop - accordionRef.current.parentNode.scrollTop + accordionRef.current.offsetHeight <= accordionRef.current.parentNode.offsetHeight) {
        // if element fits in the box when expanded, don't move the view
        return;
      } else if (accordionRef.current.offsetHeight > accordionRef.current.parentNode.offsetHeight) {
        // if element is too high to fit in the box move the view to the start of the element
        accordionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      } else {
        // move the view to the end of the element
        accordionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'end'
        });
      }
    }
  }, [selectedBuildingId]); // when selected building changed

  return /*#__PURE__*/jsxs(StyledAccordion, _objectSpread$2(_objectSpread$2({
    "data-cy": "BuildingAccordion",
    ref: accordionRef,
    display: "flex",
    flexDirection: "column"
  }, omittedProps), {}, {
    children: [/*#__PURE__*/jsx(Tooltip, {
      content: name,
      zIndex: true,
      "aria-hidden": "true",
      children: /*#__PURE__*/jsx(BuildingRow, {
        id: id,
        name: name,
        selectedBuildingId: selectedBuildingId,
        onBuildingClick: () => onBuildingClick(id),
        resultsNumber: resultsNumber
      })
    }), selectedBuildingId && selectedBuildingId === id && /*#__PURE__*/jsx(StyledBox, {
      position: "relative",
      children: /*#__PURE__*/jsx(LevelsList, {
        ref: levelsRef,
        levels: levels,
        onLevelClick: onLevelClick,
        selectedLevelId: selectedLevelId,
        buildingName: name
      })
    })]
  }));
});
BuildingAccordion.displayName = 'Building Accordion';
BuildingAccordion.defaultProps = {};
BuildingAccordion.propTypes = _objectSpread$2(_objectSpread$2(_objectSpread$2(_objectSpread$2(_objectSpread$2(_objectSpread$2(_objectSpread$2(_objectSpread$2(_objectSpread$2(_objectSpread$2(_objectSpread$2({}, styledSystemPropTypes.space), styledSystemPropTypes.color), styledSystemPropTypes.background), styledSystemPropTypes.border), styledSystemPropTypes.flexbox), styledSystemPropTypes.grid), styledSystemPropTypes.layout), styledSystemPropTypes.position), styledSystemPropTypes.shadow), styledSystemPropTypes.typography), {}, {
  as: PropTypes.string,
  forwardAs: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  resultsNumber: PropTypes.number,
  levels: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    details: PropTypes.string.isRequired,
    resultsNumber: PropTypes.number
  })),
  selectedBuildingId: PropTypes.string,
  selectedLevelId: PropTypes.string,
  onBuildingClick: PropTypes.func,
  onLevelClick: PropTypes.func
});

const _excluded$1 = ["buildings", "selectedBuildingId", "selectedLevelId", "onBuildingClick", "onLevelClick"];
function ownKeys$1(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const BuildingsWrapper = styled(Box).withConfig({
  displayName: "buildings-list__BuildingsWrapper",
  componentId: "sc-1myjbyc-0"
})(["filter:drop-shadow(", ");border-radius:", ";"], theme('shadows.md'), theme('radii.lg'));
const Arrow = props => /*#__PURE__*/jsx(Box, {
  "aria-hidden": "true",
  position: "absolute",
  display: "flex",
  top: "-9px",
  right: "8px",
  height: "9px",
  width: "19px",
  children: /*#__PURE__*/jsx("svg", {
    "aria-hidden": "true",
    width: "19",
    height: "9",
    viewBox: "0 0 19 9",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: /*#__PURE__*/jsx("path", {
      opacity: props.opacity,
      d: "M7.99485 0.720175C8.79167 -0.19048 10.2083 -0.190479 11.0052 0.720175L18.25 9H0.75L7.99485 0.720175Z",
      fill: props.iconFill
    })
  })
});
const BuildingsList = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      buildings,
      selectedBuildingId,
      selectedLevelId,
      onBuildingClick,
      onLevelClick
    } = props,
    omittedProps = _objectWithoutProperties(props, _excluded$1);
  const themeContext = useContext(ThemeContext);
  return /*#__PURE__*/jsxs(Box, _objectSpread$1(_objectSpread$1({
    "data-cy": "BuildingsListView",
    ref: ref,
    position: "relative",
    height: "auto",
    maxHeight: "100%",
    minHeight: "0",
    display: "flex",
    flexDirection: "column"
  }, omittedProps), {}, {
    children: [/*#__PURE__*/jsx(Arrow, {
      iconFill: themeContext.colors.mapNavBackground,
      opacity: selectedBuildingId === buildings[0].id ? '1' : '0.85'
    }), /*#__PURE__*/jsx(BuildingsWrapper, {
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      children: /*#__PURE__*/jsx(Box, {
        "data-cy": "ScrollableBuildingsList",
        overflow: "auto",
        height: "auto",
        maxHeight: "100%",
        role: "listbox",
        "aria-label": "Building list dropdown",
        id: "Building dropdown",
        children: buildings.map(building => /*#__PURE__*/jsx(BuildingAccordion, _objectSpread$1({
          "data-cy": "BuildingAccordion",
          selectedBuildingId: selectedBuildingId,
          selectedLevelId: selectedLevelId,
          onBuildingClick: onBuildingClick,
          onLevelClick: onLevelClick
        }, building), building.id))
      })
    })]
  }));
});
BuildingsList.displayName = 'Building Accordion';
BuildingsList.defaultProps = {};
BuildingsList.propTypes = _objectSpread$1(_objectSpread$1(_objectSpread$1(_objectSpread$1(_objectSpread$1(_objectSpread$1(_objectSpread$1(_objectSpread$1(_objectSpread$1(_objectSpread$1(_objectSpread$1({}, styledSystemPropTypes.space), styledSystemPropTypes.color), styledSystemPropTypes.background), styledSystemPropTypes.border), styledSystemPropTypes.flexbox), styledSystemPropTypes.grid), styledSystemPropTypes.layout), styledSystemPropTypes.position), styledSystemPropTypes.shadow), styledSystemPropTypes.typography), {}, {
  as: PropTypes.string,
  forwardAs: PropTypes.string,
  selectedBuildingId: PropTypes.string,
  selectedLevelId: PropTypes.string,
  onBuildingClick: PropTypes.func,
  onLevelClick: PropTypes.func
});
Arrow.propTypes = {
  opacity: PropTypes.string,
  iconFill: PropTypes.string
};

const _excluded = ["buildings", "selectedBuildingId", "selectedLevelId", "onBuildingClick", "onLevelClick", "currentBuildingName", "currentLevelName", "fallbackBuildingName", "fallbackLevelName", "isExpanded", "onTriggerClick", "withSearchResults", "tooltipContent"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const BuildingLevelSelector = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      buildings,
      selectedBuildingId,
      selectedLevelId,
      onBuildingClick,
      onLevelClick,
      currentBuildingName,
      currentLevelName,
      fallbackBuildingName,
      fallbackLevelName,
      isExpanded,
      onTriggerClick,
      withSearchResults,
      tooltipContent
    } = props,
    omittedProps = _objectWithoutProperties(props, _excluded);
  return /*#__PURE__*/jsxs(Box, _objectSpread(_objectSpread({
    ref: ref,
    display: "flex",
    flexDirection: "column",
    width: "180px",
    height: "100%"
  }, omittedProps), {}, {
    children: [/*#__PURE__*/jsx(Trigger, {
      currentBuildingName: currentBuildingName,
      currentLevelName: currentLevelName,
      fallbackBuildingName: fallbackBuildingName,
      fallbackLevelName: fallbackLevelName,
      onTriggerClick: onTriggerClick,
      isExpanded: isExpanded,
      withSearchResults: withSearchResults,
      tooltipContent: tooltipContent,
      paddingBottom: "16px"
    }), isExpanded && /*#__PURE__*/jsx(BuildingsList, {
      buildings: buildings,
      selectedBuildingId: selectedBuildingId,
      selectedLevelId: selectedLevelId,
      onBuildingClick: onBuildingClick,
      onLevelClick: onLevelClick
    })]
  }));
});
BuildingLevelSelector.displayName = 'Building Level Selector';
BuildingLevelSelector.defaultProps = {
  'data-cy': 'BuildingLevelSelector',
  fallbackBuildingName: 'Select a Building...',
  fallbackLevelName: '...',
  tooltipContent: 'Show Buildings & Levels'
};
BuildingLevelSelector.propTypes = _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({}, styledSystemPropTypes.space), styledSystemPropTypes.color), styledSystemPropTypes.background), styledSystemPropTypes.border), styledSystemPropTypes.flexbox), styledSystemPropTypes.grid), styledSystemPropTypes.layout), styledSystemPropTypes.position), styledSystemPropTypes.shadow), styledSystemPropTypes.typography), {}, {
  as: PropTypes.string,
  forwardAs: PropTypes.string,
  buildings: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    resultsNumber: PropTypes.number,
    levels: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      details: PropTypes.string.isRequired,
      resultsNumber: PropTypes.number
    }))
  })).isRequired,
  selectedBuildingId: PropTypes.string,
  selectedLevelId: PropTypes.string,
  onBuildingClick: PropTypes.func.isRequired,
  onLevelClick: PropTypes.func.isRequired,
  currentBuildingName: PropTypes.string,
  currentLevelName: PropTypes.string,
  fallbackBuildingName: PropTypes.string,
  fallbackLevelName: PropTypes.string,
  isExpanded: PropTypes.bool,
  onTriggerClick: PropTypes.func.isRequired,
  tooltipContent: PropTypes.string,
  withSearchResults: PropTypes.bool
});

export default BuildingLevelSelector;
